/**
 * Maps raw GUI element data to visible context element instances.
 * Provides type-specific mappers for buttons and cards.
 */
import { evaluateExpression } from "./context-expression.ts";
import {
    ContextGuiButtonElement,
    ContextGuiCardElement,
    ContextGuiElement,
    ElementMapper,
    ExpressionContext,
    GuiButtonElement,
    GuiCardElement,
    GuiElement,
} from "./context.types.ts";

const MAX_VISIBLE_ELEMENTS = 6;

const resolveLocalizedValue = (localizedField: Record<string, string>, language: string): string =>
    localizedField[language] || localizedField["en"] || "";

/** Evaluates shared fields: visibility, disabled state, and resolved URL */
const evaluateExpressionFields = async (uiElement: GuiElement, context: ExpressionContext) => {
    const isVisible = (await evaluateExpression<boolean>(uiElement.showExpression, context)) ?? false;
    if (!isVisible) return null;

    const disabled = !(uiElement.activeExpression
        ? ((await evaluateExpression<boolean>(uiElement.activeExpression, context)) ?? false)
        : false);

    const url = (await evaluateExpression<string>(uiElement.urlExpression, context)) ?? uiElement.urlExpression;

    return { disabled, url };
};

/** Maps a raw GUI element to a resolved ContextGuiButtonElement */
export const mapButtonElement: ElementMapper<ContextGuiButtonElement> = async (uiElement, context, language) => {
    const common = await evaluateExpressionFields(uiElement, context);
    if (!common) return null;

    const buttonElement = uiElement as GuiButtonElement;

    return {
        id: uiElement.id,
        url: common.url,
        disabled: common.disabled,
        symbol: uiElement.symbol,
        openInCurrentTab: uiElement.openInCurrentTab,
        label: resolveLocalizedValue(buttonElement.label, language),
        tooltip: resolveLocalizedValue(buttonElement.tooltip, language),
    };
};

/** Maps a raw GUI element to a resolved ContextGuiCardElement */
export const mapCardElement: ElementMapper<ContextGuiCardElement> = async (uiElement, context, language) => {
    const common = await evaluateExpressionFields(uiElement, context);
    if (!common) return null;

    const cardElement = uiElement as GuiCardElement;

    return {
        id: uiElement.id,
        url: common.url,
        symbol: uiElement.symbol,
        disabled: common.disabled,
        openInCurrentTab: uiElement.openInCurrentTab,
        text: resolveLocalizedValue(cardElement.text, language),
        title: resolveLocalizedValue(cardElement.title, language),
        buttonLabel: resolveLocalizedValue(cardElement.buttonLabel, language),
    };
};

/**
 * Transforms raw UI elements into visible context elements using the provided mapper.
 * - Evaluates visibility first to skip unnecessary work
 * - Limits output to {@link MAX_VISIBLE_ELEMENTS}
 */
export const buildContextElements = async <TOutput extends ContextGuiElement>(
    uiElements: GuiElement[],
    mergedContext: ExpressionContext,
    language: string,
    mapper: ElementMapper<TOutput>
): Promise<TOutput[]> => {
    const visibleElements: TOutput[] = [];

    for (const uiElement of uiElements) {
        if (visibleElements.length >= MAX_VISIBLE_ELEMENTS) break;

        const element = await mapper(uiElement, mergedContext, language);
        if (element) visibleElements.push(element);
    }

    return visibleElements;
};
